import { useEffect, useRef, useState } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import Loader from "../component/Loader";
import MessageToster from "../component/MessageToster";
import axios from "axios";
import { BASE_URL } from "../api";
import { closeModal } from "../utils/modal";
import { useHandleError } from "../hooks/useHandleError";
import { getApiHeaders } from "../utils/helpers";
import { getAuthToken } from "../token";
import Cookies from "js-cookie";
import { exportToExcel } from "../utils/exportData";
import { formatDateDDMMYYYY } from "../utils/dateUtils";
import CommonTable from "../component/CommonTable";
import ExportButton from "../component/ExportButton";
import MobileFilter from "../component/MobileFilter";
import CustomSelect from "../component/CustomSelect";
import copy from "copy-to-clipboard";

// Trading platforms offered in the "Open Live Account" form
const PLATFORM_OPTIONS = ["MT5"];
// Leverage offered for live accounts. Add more values here (e.g. "100", "200") to offer them again.
const LIVE_LEVERAGE_OPTIONS = ["500"];

export default function LiveAccount() {
  const token = getAuthToken();
  const handleError = useHandleError();
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState({
    platform: "",
    account_group: "",
    leverage: "",
  })
  const [validations, setValidations] = useState({
    platform: "",
    account_group: "",
    leverage: "",
  });
  const validationRules = {
    platform: "Platform is required.",
    account_group: "Account type is required.",
    leverage: "Leverage is required.",

  };

  // Empty form, with any field that has only one possible choice already selected
  const getDefaultInput = () => ({
    platform: PLATFORM_OPTIONS.length === 1 ? PLATFORM_OPTIONS[0] : "",
    account_group: accountGroupOptions.length === 1 ? accountGroupOptions[0].value : "",
    leverage: LIVE_LEVERAGE_OPTIONS.length === 1 ? LIVE_LEVERAGE_OPTIONS[0] : ""
  });

  const handleReset = () => {
    setInput(getDefaultInput());

    setValidations({
      platform: "",
      account_group: "",
      leverage: "",
    });
  }

  const [kycStatus, setKycStatus] = useState(null);

  // Details of the account just created (shown in the "Account Details" popup). null = popup closed.
  const [createdAccount, setCreatedAccount] = useState(null);

  const [copiedKey, setCopiedKey] = useState(null);

  const createdAccountRows = createdAccount
    ? [
      { key: "loginid", label: "Login ID", value: createdAccount.loginid },
      { key: "account_type", label: "Account Type", value: createdAccount.account_type },
      { key: "server_name", label: "Server Name", value: createdAccount.server_name },
      { key: "trading_password", label: "Trading Password", value: createdAccount.trading_password, isPassword: true },
      { key: "investor_password", label: "Investor Password", value: createdAccount.investor_password, isPassword: true },
    ]
    : [];

  // Which password rows are currently visible in the popup (hidden by default)
  const [visiblePasswords, setVisiblePasswords] = useState({});

  const togglePasswordVisibility = (key) => {
    setVisiblePasswords((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCopy = (text, key, label = "Text") => {
    if (text === "" || text === null || text === undefined) return;
    copy(String(text).trim());
    setCopiedKey(key);
    MessageToster("success", `${label} copied to clipboard!`);
    setTimeout(() => {
      setCopiedKey(null);
    }, 2000);
  };

  const closeAccountDetails = () => {
    setCreatedAccount(null);
    setCopiedKey(null);
    setVisiblePasswords({});
  };

  const handleDashboardClick = (accountNumber) => {
    sessionStorage.setItem("live_account_no", accountNumber);
    window.location.href = "/live_account_dashboard";
  };

  const handleAccountTradesClick = (account_number) => {
    sessionStorage.setItem("selected_account", account_number);
    window.location.href = "/trade_history";
  };


  const handelChange = (e) => {
    const { name, value } = e.target;
    setInput((prev) => ({ ...prev, [name]: value }));
    setValidations((prev) => ({ ...prev, [name]: "" }));
    // setInput({ ...input, [name]: value });
    // setValidations({ ...validations, [name]: false });
  };
  const [accountType, setAccountType] = useState([]);

  // Dropdown options for Account Type.
  // value = full MT5 group name (sent to the API unchanged).
  // label = plan name shown to the user: API "plan_name" if sent, otherwise the part of
  //         group_name after the "\" (e.g. "SeptaFX-GOLD_A\ECN-0-USD" -> "ECN-0-USD").
  const accountGroupOptions = (() => {
    const groups = (Array.isArray(accountType) ? accountType : []).filter((group) => group && group.group_name);
    const planNameOf = (group) => group.plan_name || String(group.group_name).split("\\").pop();
    const serverOf = (group) => String(group.group_name).split("\\")[0];

    const planCount = {};
    groups.forEach((group) => {
      const plan = planNameOf(group);
      planCount[plan] = (planCount[plan] || 0) + 1;
    });

    return groups.map((group) => {
      const plan = planNameOf(group);
      // Same plan name on two servers: add the server so the user can tell them apart
      const label = planCount[plan] > 1 && String(group.group_name).includes("\\")
        ? `${plan} (${serverOf(group)})`
        : plan;
      return { label, value: group.group_name };
    });
  })();

  // When the account types arrive (or change), pre-select the only choice if nothing is chosen yet
  useEffect(() => {
    setInput((prev) => ({
      ...prev,
      platform: prev.platform || (PLATFORM_OPTIONS.length === 1 ? PLATFORM_OPTIONS[0] : ""),
      account_group:
        prev.account_group || (accountGroupOptions.length === 1 ? accountGroupOptions[0].value : ""),
      leverage: prev.leverage || (LIVE_LEVERAGE_OPTIONS.length === 1 ? LIVE_LEVERAGE_OPTIONS[0] : ""),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountType]);

  const getaccounttypes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/MT5M/get_mt5_groups_for_user`, { headers: getApiHeaders() });

      if (res.data.code === 200) {
        const data = res.data.data;
        const groups = Array.isArray(data) ? data : [data];

        setAccountType(groups);
      } else {
        MessageToster('error', res.data.message);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const onSubmitForm = async () => {
    const newValidations = {};
    let hasError = false;

    Object.keys(validationRules).forEach((field) => {
      if (!input[field]) {
        newValidations[field] = validationRules[field];
        hasError = true;
      } else {
        newValidations[field] = "";
      }
    });

    setValidations(newValidations);
    if (hasError) return;
    await createLiveAccount();

    // for (const field in input) {
    //   if (!input[field]) {
    //     newValidations[field] = true;
    //     hasError = true;
    //   } else {
    //     newValidations[field] = false;
    //   }
    // }
  };

  const createLiveAccount = async () => {
    const mid = sessionStorage.getItem("mid");

    const bodyData = {
      // mid: Number(mid),
      trading_platform: input.platform,
      currency: "USD",
      account_group: input.account_group,
      leverage: Number(input.leverage),
      account_type: "LIVE"
    };

    setLoading(true);

    try {
      const res = await axios.post(`${BASE_URL}/MT5M/create_new_real_account`, bodyData, { headers: getApiHeaders() });

      if (res.data.code === 200) {
        const data = res.data;
        const accountTypeValue = data.account_type || input.account_group;
        const selectedGroup = accountGroupOptions.find((option) => option.value === accountTypeValue);

        MessageToster("success", data.message);
        // Keep the credentials sent by the API so they can be shown in the details popup
        setCreatedAccount({
          loginid: data.loginid ?? "",
          account_type: selectedGroup ? selectedGroup.label : String(accountTypeValue || "").split("\\").pop(),
          server_name: data["server name"] ?? data.server_name ?? "",
          trading_password: data.Trading_password ?? data.trading_password ?? "",
          investor_password: data.investor_password ?? "",
        });
        handleReset();
        getTabelData();
        closeModal();
      } else {
        MessageToster("error", res.data.message);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const checkKycStatus = async () => {
    const mid = sessionStorage.getItem("mid");

    setLoading(true);

    // const inputBody = {
    //   mid: Number(mid)
    // };

    try {
      const res = await axios.get(`${BASE_URL}/kyc/kyc_approve_or_reject_status`, { headers: getApiHeaders() });
      if (res.data.code === 200) {
        const status = res.data.data?.status || 'Pending';
        setKycStatus(status);
        // setKycStatus("Approve");
        // console.log(status)
      }
      else {
        setKycStatus("NotSubmitted");
        MessageToster("error", res.data.message || "Failed to fetch KYC status.");
      }

    } catch (error) {
      if (error.response && error.response.status === 404) {
        setKycStatus("NotSubmitted");
      } else {
        handleError(error);
        MessageToster("error", "Something went wrong");
      }

    } finally {
      setLoading(false);
    }
  };

  const [accountData, setAccountData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
  });

  const clearFilters = () => {
    setFilters({
      fromDate: "",
      toDate: "",
    });
    setSearchTerm("");
    setFilteredData(accountData);
  };

  useEffect(() => {
    setFilteredData(accountData);
  }, [accountData]);

  const handleSearch = () => {
    let data = [...accountData];
    if (filters.fromDate) {
      const from = new Date(filters.fromDate);
      from.setHours(0, 0, 0, 0);
      data = data.filter((item) => {
        const itemDateStr = item.create_date ? item.create_date.split('.')[0] : "";
        const itemDate = new Date(itemDateStr);
        return itemDate >= from;
      });
    }
    if (filters.toDate) {
      const to = new Date(filters.toDate);
      to.setHours(23, 59, 59, 999);
      data = data.filter((item) => {
        const itemDateStr = item.create_date ? item.create_date.split('.')[0] : "";
        const itemDate = new Date(itemDateStr);
        return itemDate <= to;
      });
    }
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      data = data.filter((item) => {
        return (
          String(item.account_number || "").toLowerCase().includes(searchLower) ||
          String(item.account_group || "").toLowerCase().includes(searchLower) ||
          String(item.leverage || "").toLowerCase().includes(searchLower) ||
          String(item.margin_free || "").toLowerCase().includes(searchLower) ||
          String(item.balance || "").toLowerCase().includes(searchLower)
        );
      });
    }
    setFilteredData(data);
  };

  const getTabelData = async () => {
    const mid = sessionStorage.getItem('mid');

    // const bodyData = {
    //   mid: Number(mid),
    //   mode: ""
    // }

    setLoading(true);

    try {
      const res = await axios.get(`${BASE_URL}/MT5M/user_real_accounts_list`, { headers: getApiHeaders() });

      if (res.data.code === 200) {
        setAccountData(res.data.data || []);
      } else {
        MessageToster("error", res.data.message);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false)
    }
  }

  // useEffect(() => {
  //   checkKycStatus();
  //   getaccounttypes()
  //   getTabelData()
  // }, []);

  useEffect(() => {
    checkKycStatus();
  }, []);

  useEffect(() => {
    if (kycStatus === "Approved") {
      getaccounttypes();
      getTabelData();
    }
  }, [kycStatus]);

  // useEffect(() => {
  //   if (accountData?.length > 0) {
  //     const rows = accountData.map((acc) => [
  //       acc.account_number,
  //       acc.trading_platform,
  //       acc.account_type,
  //       acc.leverage,
  //       acc.currency,
  //       acc.create_date ? dayjs(acc.create_date).format("DD-MM-YYYY") : "",
  //       acc.istatus,
  //       "",
  //     ]);
  //     displayButtons(tableRef, rows);
  //   }
  // }, [accountData]);



  const isApprove = kycStatus === "Approved";
  const isReject = kycStatus === "Reject";
  const isPending = kycStatus === "Pending";
  const isExpire = kycStatus === "Expire";
  const isNotSubmitted = kycStatus === "NotSubmitted";


  return (
    <>
      <div id="layout-wrapper">
        {loading &&
          <Loader />
        }
        <Topbar />
        <Sidebar />
        <div className="main-content">
          <div className="page-content">
            {isPending && (
              <div className="row justify-content-center">
                <div
                  className="alert alert-warning alert-dismissible fade show"
                  role="alert"
                  style={{
                    marginBottom: "1.5rem",
                  }}
                >
                  <i className="mdi mdi-timer-sand-full me-2" />
                  Please complete your KYC process. Once your verification is approved, you will be able to open a live account on MT5.
                </div>

                {/* <div className="col-xl-5 col-lg-6 col-md-8 col-12">
                  <div className="card">
                    <div className="card-body text-center">
                      <img
                        src="assets/images/ib/overview.webp"
                        alt=""
                        width="280px"
                      />
                      <p className="fw-bold text-warning">
                        IB Application Submitted
                      </p>
                      <button
                        type="button"
                        className="btn btn-primary waves-effect waves-light"
                        disabled
                      >
                        Application Pending Review
                      </button>
                    </div>
                  </div>
                </div> */}
              </div>
            )}

            {isReject && (
              <div className="row justify-content-center">
                <div
                  className="alert alert-danger alert-dismissible fade show"
                  role="alert"
                  style={{
                    marginBottom: "1.5rem",
                  }}
                >
                  <i className="mdi mdi-close-octagon-outline me-2" />
                  Your KYC application has been <b>rejected</b>. Please check your email for details regarding the required documents or information, and re-submit your application.
                </div>

              </div>
            )}
            {isApprove && (
              <>
                <div className="container-fluid">
                  {/* start page title */}
                  <div className="row">
                    <div className="col-12">
                      <div className="page-title-box d-flex align-items-center justify-content-between">
                        <h2 className="fw-700 mb-0">Live Account</h2>
                        <div className="live_account_btn">
                          <button
                            type="button"
                            className="btn btn-primary waves-effect waves-light"
                            data-bs-toggle="modal"
                            data-bs-target=".bs-example-modal-center"
                            onClick={handleReset}
                          >
                            Open Account
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* end page title */}
                  <div className="row">
                    <div className="col-12">
                      <div className="card" style={{ minHeight: "calc(100vh - 210px)" }}>
                        <div className="card-body liveaccount_body d-flex flex-column">
                          <div className="d-flex flex-wrap align-items-end gap-2 mb-4 desktop-filter-row">
                            <div>
                              <label htmlFor="">From</label>
                              <input
                                className="form-control"
                                type="date"
                                onKeyDown={(e) => e.preventDefault()}
                                name="fromDate"
                                value={filters.fromDate}
                                onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
                                max={filters.toDate ? (filters.toDate > new Date().toISOString().split("T")[0] ? new Date().toISOString().split("T")[0] : filters.toDate) : new Date().toISOString().split("T")[0]}
                                id="example-date-input"
                              />
                            </div>
                            <div>
                              <label htmlFor="">To</label>
                              <input
                                className="form-control"
                                type="date"
                                onKeyDown={(e) => e.preventDefault()}
                                name="toDate"
                                value={filters.toDate}
                                onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
                                min={filters.fromDate || undefined}
                                max={new Date().toISOString().split("T")[0]}
                                id="example-date-input"
                              />
                            </div>
                            <div>
                              <label htmlFor="">Search</label>
                              <input
                                className="form-control"
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                              />
                            </div>
                            <div>
                              <button
                                type="button"
                                className="btn btn-primary waves-effect waves-light"
                                onClick={handleSearch}
                              >
                                Search
                              </button>
                            </div>
                            <div>
                              <button
                                type="button"
                                className="btn btn-primary waves-effect waves-light"
                                onClick={clearFilters}
                                disabled={!filters.fromDate && !filters.toDate && !searchTerm}
                              >
                                Clear
                              </button>
                            </div>
                            <div className="ms-auto">
                              <ExportButton
                                disabled={!filteredData || filteredData.length === 0}
                                onClick={() => exportToExcel(filteredData, "live_account", "live_account")}
                              />
                            </div>
                          </div>

                          <MobileFilter
                            title="History"
                            onExport={() => exportToExcel(filteredData, "live_account", "live_account")}
                            exportDisabled={!filteredData || filteredData.length === 0}
                          >
                            <div>
                              <label htmlFor="">From</label>
                              <input
                                className="form-control"
                                type="date"
                                onKeyDown={(e) => e.preventDefault()}
                                name="fromDate"
                                value={filters.fromDate}
                                onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
                                max={filters.toDate ? (filters.toDate > new Date().toISOString().split("T")[0] ? new Date().toISOString().split("T")[0] : filters.toDate) : new Date().toISOString().split("T")[0]}
                              />
                            </div>
                            <div>
                              <label htmlFor="">To</label>
                              <input
                                className="form-control"
                                type="date"
                                onKeyDown={(e) => e.preventDefault()}
                                name="toDate"
                                value={filters.toDate}
                                onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
                                min={filters.fromDate || undefined}
                                max={new Date().toISOString().split("T")[0]}
                              />
                            </div>
                            <div>
                              <label htmlFor="">Search</label>
                              <input
                                className="form-control"
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                              />
                            </div>
                            <div className="d-flex gap-2 mt-2">
                              <button
                                type="button"
                                className="btn btn-primary waves-effect waves-light flex-grow-1"
                                onClick={handleSearch}
                                data-bs-dismiss="offcanvas"
                                data-bs-target="#filterOffcanvasMobile"
                              >
                                Search
                              </button>
                              <button
                                type="button"
                                className="btn btn-light waves-effect waves-light border flex-grow-1"
                                onClick={clearFilters}
                                disabled={!filters.fromDate && !filters.toDate && !searchTerm}
                                data-bs-dismiss="offcanvas"
                                data-bs-target="#filterOffcanvasMobile"
                              >
                                Clear
                              </button>
                            </div>
                          </MobileFilter>
                          <CommonTable
                            columns={[
                              { header: "Account No", accessor: "account_number" },
                              { header: "Platform", accessor: "trading_platform" },
                              { header: "Account Type", accessor: "account_type" },
                              { header: "Leverage", accessor: "leverage" },
                              { header: "Currency", accessor: "currency" },
                              { header: "Date", render: (row) => row.create_date ? formatDateDDMMYYYY(row.create_date) : "" },
                              { header: "Status", accessor: "istatus" },
                              {
                                header: "Actions",
                                headerStyle: { width: "1%", whiteSpace: "nowrap" },
                                style: { width: "1%", whiteSpace: "nowrap" },
                                render: (row, index, meta) => {
                                  const isLastTwo = meta?.isLastTwo ?? (
                                    filteredData && filteredData.length > 1
                                      ? (index >= filteredData.length - 2)
                                      : true
                                  );

                                  return (
                                    <div className={`btn-group ${isLastTwo ? "dropup" : ""}`}>
                                      <button
                                        type="button"
                                        className="btn btn-primary dropdown-toggle"
                                        data-bs-toggle="dropdown"
                                        data-bs-boundary="viewport"
                                        aria-haspopup="true"
                                        aria-expanded="false"
                                      >
                                        Actions <i className={`mdi ${isLastTwo ? "mdi-chevron-up" : "mdi-chevron-down"}`}></i>
                                      </button>
                                      <div className="dropdown-menu dropdown-menu-end table_dropdown">
                                        {/* <button className="dropdown-item" onClick={() => handleDashboardClick(row.account_number)}>Live Account Dashboard</button> */}
                                        {/* <a className="dropdown-item" href="/leverage_history">Leverage Management</a> */}
                                        <a className="dropdown-item" href={`/masterpassword?account=${row.account_number}&type=live`}>Change Trading Password</a>
                                        <a className="dropdown-item" href={`/investorpassword?account=${row.account_number}&type=live`}>Change Investor Password</a>
                                      </div>
                                    </div>
                                  );
                                }
                              }
                            ]}
                            data={filteredData || []}
                            loading={loading}
                            containerStyle={{ overflow: "visible" }}
                            className="flex-grow-1 d-flex flex-column justify-content-between"
                          />
                        </div>
                      </div>
                    </div>{" "}
                    {/* end col */}
                    <div>
                      {/* modal */}
                      <div
                        className="modal fade bs-example-modal-center"
                        tabIndex={-1}
                        role="dialog"
                        aria-hidden="true"
                        data-bs-backdrop="static"
                        data-bs-keyboard="false"
                      >
                        <div className="modal-dialog modal-dialog-centered">
                          <div className="modal-content">
                            <div className="modal-header">
                              <h5 className="modal-title">Open Live Account</h5>
                              <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                              />
                            </div>
                            <div className="modal-body">
                              <div className="row">
                                <div className="col-12 mb-3">
                                  <div>
                                    <label className="form-label">
                                      Trading Platform
                                      <span className="text-danger">*</span>
                                    </label>
                                    <CustomSelect
                                      id="platform"
                                      name="platform"
                                      value={input.platform}
                                      options={PLATFORM_OPTIONS}
                                      onChange={handelChange}
                                      placeholder="Select Your Trading Platform"
                                      hasError={!!validations.platform}
                                    />
                                    {validations.platform && (
                                      <p className="text-danger">{validations.platform}</p>
                                    )}
                                    {/* {validations.platform && (
                                      <p className="text-danger mb-0">{validationRules.platform}</p>
                                    )} */}
                                  </div>
                                </div>
                                <div className="col-12 mb-3">
                                  <div>
                                    <label className="form-label">
                                      Account Type{" "}
                                      <span className="text-danger">*</span>
                                    </label>
                                    {/* Searchable list: the API returns many MT5 groups */}
                                    <div className="notranslate" translate="no">
                                      <CustomSelect
                                        id="account_group"
                                        name="account_group"
                                        value={input.account_group}
                                        options={accountGroupOptions}
                                        onChange={handelChange}
                                        placeholder="Select account type"
                                        searchable={accountGroupOptions.length > 8}
                                        searchPlaceholder="Search account type..."
                                        hasError={!!validations.account_group}
                                      />
                                    </div>
                                    {validations.account_group && (
                                      <p className="text-danger">{validations.account_group}</p>
                                    )}
                                    {/* {validations.account_group && (
                                      <p className="text-danger mb-0">{validationRules.account_group}</p>
                                    )} */}
                                    {/* {validations.account_group &&
                                  verror && <>
                                    <p className='text-danger mb-0'>{verror}</p>
                                  </>
                                } */}
                                  </div>
                                </div>
                                {/* <div className="col-12 mb-3">
                              <div>
                                <label className="form-label">
                                  Currency<span className="text-danger">*</span>
                                </label>
                                <select className="form-select" name="select">
                                  <option value={0}>Select an Option</option>
                                  <option value="Septa-STD-CRM-DEMO">
                                    Septa-STD-CRM-DEMO
                                  </option>
                                  <option value="Septa-VIP-CRM-DEMO">
                                    Septa-VIP-CRM-DEMO
                                  </option>
                                  <option value="Septa-ECN-CRM-DEMO">
                                    Septa-ECN-CRM-DEMO
                                  </option>
                                </select>
                              </div>
                            </div> */}
                                <div className="col-12 mb-3">
                                  <div>
                                    <label className="form-label">
                                      Leverage
                                      <span className="text-danger">*</span>
                                    </label>
                                    <CustomSelect
                                      id="leverage"
                                      name="leverage"
                                      value={input.leverage}
                                      options={LIVE_LEVERAGE_OPTIONS}
                                      onChange={handelChange}
                                      placeholder="Select leverage"
                                      hasError={!!validations.leverage}
                                    />
                                    {/* {validations.leverage && (
                                      <p className="text-danger mb-0">{validationRules.leverage}</p>
                                    )} */}
                                    {validations.leverage && (
                                      <p className="text-danger">{validations.leverage}</p>
                                    )}
                                    {/* {validations.leverage &&
                                  verror && <>
                                    <p className='text-danger mb-0'>{verror}</p>
                                  </>
                                } */}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="modal-footer">
                              <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => {
                                  setInput(getDefaultInput());
                                  setValidations({});
                                }}
                              >
                                Reset
                              </button>
                              <button type="button"
                                className="btn btn-primary"
                                // data-bs-dismiss="modal"
                                // aria-label="Close"
                                onClick={onSubmitForm}
                              >
                                Submit
                              </button>
                            </div>
                          </div>
                          {/* /.modal-content */}
                        </div>
                        {/* /.modal-dialog */}
                      </div>
                      {/* /.modal */}
                    </div>
                  </div>
                </div>
              </>
            )}

            {isExpire && (
              <div className="row justify-content-center">
                <div
                  className="alert alert-danger alert-dismissible fade show"
                  role="alert"
                  style={{
                    marginBottom: "1.5rem",
                  }}
                >
                  <i className="mdi mdi-timer-sand-full me-2" />
                  Your KYC has <b>expired</b>. Please complete the KYC verification process to continue. Once your verification is approved, you will be able to open a live MT5 account.</div>
              </div>
            )}
            {isNotSubmitted && (
              <div className="row justify-content-center">
                <div
                  className="alert alert-warning alert-dismissible fade show"
                  role="alert"
                  style={{
                    marginBottom: "1.5rem",
                  }}
                >
                  <i className="mdi mdi-timer-sand-full me-2" />
                  It looks like you haven’t submitted your KYC yet. Please upload your KYC details to begin verification.</div>
              </div>
            )}
            {/* container-fluid */}
          </div>

          {/* End Page-content */}
          <Footer />
        </div>
      </div >

      {/* Account Details popup: shown after a live account is created. Closes only from its own buttons. */}
      {createdAccount && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.55)", zIndex: 1060 }}
          />
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            style={{ zIndex: 1065 }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" style={{ borderRadius: "16px", overflow: "hidden" }}>
                <div className="modal-header border-bottom-0 pb-0">
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={closeAccountDetails}
                  />
                </div>
                <div className="modal-body px-4 pt-0 pb-3">
                  <div className="text-center mb-4">
                    <div
                      className="d-inline-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10 text-success mb-3"
                      style={{ width: "72px", height: "72px" }}
                    >
                      <i className="mdi mdi-check-bold" style={{ fontSize: "36px", lineHeight: 1 }} />
                    </div>
                    <h5 className="fw-bold mb-1">Account Created Successfully</h5>
                    <p className="text-muted mb-0">Your live account is ready. Here are your login details.</p>
                  </div>

                  <div className="border rounded-3 overflow-hidden">
                    {createdAccountRows.map((row, index) => {
                      const hasValue = row.value !== "" && row.value !== null && row.value !== undefined;
                      const isHidden = row.isPassword && !visiblePasswords[row.key];

                      return (
                        <div
                          key={row.key}
                          className={`d-flex align-items-center justify-content-between gap-3 px-3 py-2 ${index % 2 === 0 ? "bg-light" : ""} ${index !== createdAccountRows.length - 1 ? "border-bottom" : ""}`}
                          style={{ minHeight: "52px" }}
                        >
                          <span className="text-muted font-size-13 text-nowrap">{row.label}</span>
                          <div className="d-flex align-items-center gap-1" style={{ minWidth: 0 }}>
                            <span
                              className="fw-semibold text-dark text-end notranslate me-1"
                              translate="no"
                              style={{ wordBreak: "break-all", letterSpacing: isHidden && hasValue ? "2px" : undefined }}
                            >
                              {!hasValue ? "-" : isHidden ? "••••••••" : row.value}
                            </span>
                            {hasValue && row.isPassword && (
                              <button
                                type="button"
                                className="btn btn-sm btn-link text-muted p-1"
                                title={isHidden ? `Show ${row.label}` : `Hide ${row.label}`}
                                aria-label={isHidden ? `Show ${row.label}` : `Hide ${row.label}`}
                                onClick={() => togglePasswordVisibility(row.key)}
                              >
                                <i className={`mdi ${isHidden ? "mdi-eye-outline" : "mdi-eye-off-outline"} font-size-18`} style={{ lineHeight: 1 }} />
                              </button>
                            )}
                            {hasValue && (
                              <button
                                type="button"
                                className="btn btn-sm btn-link text-muted p-1"
                                title={`Copy ${row.label}`}
                                aria-label={`Copy ${row.label}`}
                                onClick={() => handleCopy(row.value, row.key, row.label)}
                              >
                                <i
                                  className={`bx ${copiedKey === row.key ? "bx-check text-success" : "bx-copy"} font-size-18`}
                                  style={{ lineHeight: 1 }}
                                />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div
                    className="d-flex align-items-start gap-2 rounded-3 px-3 py-2 mt-3"
                    role="alert"
                    style={{ backgroundColor: "#fff8e6", border: "1px solid #ffe2a8", color: "#8a6100" }}
                  >
                    <i className="mdi mdi-alert-outline font-size-18" style={{ lineHeight: 1.3 }} />
                    <span className="font-size-13">
                      Please copy and save these details securely before closing this window.
                    </span>
                  </div>
                </div>
                <div className="modal-footer border-top-0 px-4 pt-0 pb-4">
                  <button type="button" className="btn btn-primary w-100 m-0" onClick={closeAccountDetails}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
